import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box } from '@mui/material'
import { useTheme } from '@mui/styles'
import PrintTicketsGrid from 'components/generic/printable/components/PrintTicketsGrid'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { formatNumber, setDecimalPrecision } from 'utils/helpers'
import {
  WEIGHT_OPTIONS,
  PACKAGE_OPTIONS,
  PAYMENT_METHODS_VALUES,
  PAYMENT_OPTIONS_OPTIONS,
  PRINT_DOCUMENT_TYPES,
} from 'utils/constants'
import usePaymentTerms from 'utils/custom-hooks/usePaymentTerms'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { Detail } from './components/Detail'
import { countryCodeToName } from 'utils/data-manipulation/valueFormatter'

const ticketHeaders = ['Material Name', 'Gross', 'Tare', 'Net', 'Price', 'Value']

interface Address {
  name: string
  address?: string
  address1?: string
  city: string
  state: string
  zip: string
  country?: string
}

const DEFAULT_ADDRESS = {
  name: '',
  city: '',
  state: '',
  zip: '',
}

const PrintableInvoice = ({
  invoiceData,
  selectedOutboundTickets,
  salesOrderData,
  customerData,
  priceSheetsData,
  priceSheet,
  summaryData,
  totalAmountPaid,
  pricePrepend,
}) => {
  const theme = useTheme()

  const [customerAddress, setCustomerAddress] = useState<Address>(DEFAULT_ADDRESS)
  const [locationAddress, setLocationAddress] = useState<Address>(DEFAULT_ADDRESS)
  const { paymentTermsOptions, dueDateCalculation } = usePaymentTerms({})

  const { defaultYardUm, details } = useAppSelector(state => state.settings)
  const yardCountry = details?.country
  const { enableCountryCodes } = useAppSelector(state => state.featureFlags?.variables || {})
  const showCountry =
    enableCountryCodes && !!customerAddress.country && !!yardCountry && customerAddress.country !== yardCountry

  const ticketChargeback = selectedOutboundTickets?.reduce(
    (acc, ticket) => acc + (ticket.freightCost?.chargeBack || 0),
    0,
  )

  useEffect(() => {
    if (customerData && salesOrderData) {
      const { locations } = customerData
      const salesOrderSoldToAddress = locations.find(location => location?.id === salesOrderData?.salesOrderLocationId)
      setCustomerAddress(salesOrderSoldToAddress || locations[0])
    }
  }, [customerData, salesOrderData])

  useEffect(() => {
    if (selectedOutboundTickets?.length > 0) {
      const location = selectedOutboundTickets?.[0]?.shippingInfo
      setLocationAddress(location)
    }
  }, [selectedOutboundTickets])

  return (
    <>
      <div style={{ display: 'flex' }}>
        <div
          style={{
            display: 'flex',
            padding: theme.spacing(1),
            border: '1px solid black',
            flexBasis: '75%',
          }}
        >
          <div style={{ flexBasis: '50%' }}>
            <strong>Sold to:</strong>
            <p>
              <div>{customerData?.displayName}</div>
              <div>{customerAddress?.name}</div>
              {customerAddress?.address1 && (
                <>
                  <div>{customerAddress?.address1}</div>
                  <div>
                    {customerAddress?.city}, {customerAddress?.state} {customerAddress?.zip}
                  </div>
                  {showCountry && <div>{countryCodeToName(customerAddress.country)} </div>}
                </>
              )}
            </p>
          </div>
          <div style={{ flexBasis: '50%' }}>
            <strong>Ship to:</strong>
            <p>
              <div>
                {salesOrderData?.freightDestination?.customerName ||
                  salesOrderData?.customer?.companyName ||
                  customerData?.displayName}
              </div>
              <div>{locationAddress?.name}</div>
              <div>{locationAddress?.address}</div>
              <div>
                {locationAddress?.city}, {locationAddress?.state} {locationAddress?.zip}
              </div>
              {showCountry && <div>{countryCodeToName(locationAddress.country)} </div>}
            </p>
          </div>
        </div>
        <Box
          sx={{
            flexBasis: '25%',
            border: '1px solid black',
            marginLeft: theme.spacing(2),
            padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
            textAlign: 'right',
          }}
        >
          <strong>
            Balance: {pricePrepend}
            {setDecimalPrecision(summaryData.totalValue.minus(totalAmountPaid).toNumber(), {
              decimalPrecisionOverride: 2,
            })}
          </strong>
          <div>
            Paid: {pricePrepend}
            {setDecimalPrecision(totalAmountPaid, {
              decimalPrecisionOverride: 2,
            })}
          </div>
          {ticketChargeback ? (
            <div>
              Freight: {pricePrepend}
              {setDecimalPrecision(ticketChargeback, {
                decimalPrecisionOverride: 2,
              })}
            </div>
          ) : null}
          <div>
            Subtotal: {pricePrepend}
            {setDecimalPrecision(summaryData.subTotal.toNumber(), {
              decimalPrecisionOverride: 2,
            })}
          </div>
          <div>
            Tax: {pricePrepend}
            {setDecimalPrecision(summaryData.taxValue.toNumber(), {
              decimalPrecisionOverride: 2,
            })}
          </div>
          <div>
            Total: {pricePrepend}
            {setDecimalPrecision(summaryData.totalValue.toNumber(), {
              decimalPrecisionOverride: 2,
            })}
          </div>
          <div>{`Gross: ${formatNumber(summaryData.grossWeight)} ${defaultYardUm}`}</div>
          <div>{`Tare: ${formatNumber(summaryData.tareWeight)} ${defaultYardUm}`}</div>
          <div>{`Net: ${formatNumber(summaryData.grossWeight - summaryData.tareWeight)} ${defaultYardUm}`}</div>
        </Box>
      </div>

      <div
        style={{
          marginTop: '1em',
          padding: theme.spacing(1),
          border: '1px solid black',
          lineHeight: '2em',
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        <Detail label="Date of Issue" data={dateFormatter(invoiceData?.issueDate)} />
        <Detail
          label="Expiration Date"
          data={dateFormatter(dueDateCalculation(invoiceData?.issueDate, salesOrderData?.paymentTermId)) || ''}
        />
        <Detail
          label="Package Type"
          data={PACKAGE_OPTIONS.find(option => option.id === salesOrderData?.orderSalesDetails?.packageType)?.name}
        />
        <Detail label="Departing Yard" data={salesOrderData?.orderSalesDetails?.departingYard} />
        <Detail label="F.O.B." data={salesOrderData?.orderSalesDetails?.fairOnBoard} />
        <Detail label="Currency" data={salesOrderData?.currency} />
        <Detail
          label="Invoice Weights"
          data={WEIGHT_OPTIONS.find(option => option.id === salesOrderData?.orderSalesDetails?.invoiceWeights)?.name}
        />
        <Detail
          label="Payment Terms"
          data={paymentTermsOptions.find(option => option.id === salesOrderData?.paymentTermId)?.name || ''}
        />
        <Detail
          label="Payment Method"
          data={PAYMENT_METHODS_VALUES[salesOrderData?.paymentMethod?.toUpperCase()] || ''}
        />
        <Detail
          label="Payment Options"
          data={PAYMENT_OPTIONS_OPTIONS.find(option => option.id === salesOrderData?.paymentOptions)?.name}
        />
        <Detail label="Customer Order #" data={salesOrderData?.customerPONumber} />
      </div>

      <div
        style={{
          border: '1px solid black',
          marginTop: '1em',
          padding: theme.spacing(1),
        }}
      >
        <PrintTicketsGrid
          type={PRINT_DOCUMENT_TYPES.INVOICE}
          title={`Shipment Tickets (${selectedOutboundTickets?.length || 0})`}
          headers={ticketHeaders}
          tickets={selectedOutboundTickets || []}
          priceSheet={priceSheet}
          defaultPriceSheet={priceSheetsData?.Items?.[0]}
          defaultPriceType="sellPrice"
          consumerWeights={invoiceData?.usingConsumerWeights ? invoiceData?.consumerWeights : []}
          pricePrepend={pricePrepend}
        />
      </div>

      <div
        style={{
          marginTop: '1em',
          padding: theme.spacing(1),
          border: '1px solid black',
          width: '100%',
        }}
      >
        <strong>Notes:</strong>
        <div style={{ lineHeight: '.1em' }}>
          <p>{invoiceData?.notes}</p>
        </div>
      </div>
    </>
  )
}

PrintableInvoice.propTypes = {
  invoiceData: PropTypes.object.isRequired,
  selectedOutboundTickets: PropTypes.array.isRequired,
  salesOrderData: PropTypes.object.isRequired,
  customerData: PropTypes.object.isRequired,
  priceSheetsData: PropTypes.object.isRequired,
  priceSheet: PropTypes.object.isRequired,
  summaryData: PropTypes.object.isRequired,
  totalAmountPaid: PropTypes.number,
  pricePrepend: PropTypes.string,
}

PrintableInvoice.defaultProps = {
  totalAmountPaid: 0,
  pricePrepend: '$',
}

export default PrintableInvoice
